// The October $4.99 offer was withdrawn on 25 October 2026. This page stays
// alive because the offer was emailed to members and non-members, so links are
// still out there. It no longer sells anything: it explains the offer has ended
// and, crucially, still activates any 30 days that were paid for before it
// closed (a PayPal payment in flight must never be taken without access).
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { trackOperational } from "@/lib/track";
import { useServerFn } from "@tanstack/react-start";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMembership } from "@/hooks/useMembership";
import { captureOctoberOrder } from "@/lib/october-offer.functions";

const TITLE = "The October Offer Has Ended — Recovery Trauma™";
const DESCRIPTION =
  "The one-time October offer is no longer available. Choose a membership for full access, or sign in to activate access you have already paid for.";

export const Route = createFileRoute("/october")({
  validateSearch: (s: Record<string, unknown>): { token?: string; paid?: string; cancelled?: string } => ({
    token: typeof s.token === "string" ? s.token : undefined,
    paid: s.paid != null ? String(s.paid) : undefined,
    cancelled: s.cancelled != null ? String(s.cancelled) : undefined,
  }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/october" }],
  }),
  component: OctoberPage,
});

function OctoberPage() {
  const { token, cancelled } = Route.useSearch();
  const membership = useMembership();
  const capture = useServerFn(captureOctoberOrder);

  const [error, setError] = useState("");
  const [activatedUntil, setActivatedUntil] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const captured = useRef(false);

  // Money taken before the offer was withdrawn is still honoured: returning
  // from PayPal verifies that order and activates the days that were paid for.
  useEffect(() => {
    if (!token || !membership.isAuthed || captured.current) return;
    captured.current = true;
    setConfirming(true);
    void (async () => {
      for (let i = 0; i < 4; i++) {
        const r = await capture({ data: { orderId: token } });
        if ("ok" in r && r.ok) {
          setActivatedUntil(r.expiresAt);
          membership.retry();
          break;
        }
        if ("error" in r && r.error) {
          setError(r.error);
          break;
        }
        await new Promise((res) => setTimeout(res, 2500));
      }
      setConfirming(false);
    })();
  }, [token, membership.isAuthed, capture, membership]);

  // Count each visit once (operational event, no marketing data).
  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current || token || membership.isAdmin) return;
    viewed.current = true;
    trackOperational("october_page_viewed", {}, { source: "october" });
  }, [token, membership.isAdmin]);

  const until = activatedUntil
    ? new Date(activatedUntil).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : "";

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:py-14">
      <h1 className="font-serif text-3xl leading-tight text-foreground sm:text-5xl">
        {activatedUntil ? "Your access is active" : "The October offer has ended"}
      </h1>

      <div
        aria-live="polite"
        className="mt-5 rounded-2xl border border-border bg-card p-5 shadow-sm sm:mt-8 sm:p-8"
      >
        {confirming ? (
          <p className="flex items-center gap-2 text-foreground">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> Confirming your payment with PayPal…
          </p>
        ) : activatedUntil ? (
          <div className="space-y-4">
            <p className="text-lg font-medium text-foreground">
              Thank you — your 30 days of full access are now active until {until}. 💛
            </p>
            <Button asChild size="lg">
              <Link to="/dashboard">Go to my dashboard</Link>
            </Button>
          </div>
        ) : token && !membership.isAuthed && membership.authResolved ? (
          <div className="space-y-3">
            <p className="text-foreground">
              Your PayPal payment came through. Just sign in with your account so we can add your 30 days to it —
              nothing to pay again.
            </p>
            <Button asChild size="lg">
              <Link to="/login" search={{ next: `/october?token=${encodeURIComponent(token)}` }}>
                Sign In &amp; Activate My 30 Days
              </Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-foreground">
              The October offer is no longer available to buy. If you would still like full access to Recovery
              Trauma™, you can choose a membership instead — monthly, 6-month or yearly — and anything you write or
              save stays with you.
            </p>
            <Button asChild size="lg">
              <Link to="/choose-membership">Choose a membership</Link>
            </Button>
            <p className="text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="underline">
                Log in
              </Link>{" "}
              to see what is waiting for you.
            </p>
          </div>
        )}

        {cancelled && !activatedUntil ? (
          <p className="mt-3 text-sm text-muted-foreground">Payment cancelled — nothing was charged.</p>
        ) : null}
        {error ? (
          <p role="alert" className="mt-3 text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>

      <p className="mt-10 text-xs text-muted-foreground">
        Recovery Trauma™ provides educational resources and wellbeing tools. It is not therapy, medical or a crisis
        service. If you need urgent support, please contact your local emergency services.
      </p>
    </main>
  );
}
